/**
 * primaryEventTypes — Single master registry of Celey's primary event types.
 *
 * ONE source of truth, consumed by:
 *   • CustomerLandingPage hero tiles — renders the FIRST SIX entries only,
 *     preserving the tight landing composition.
 *   • PlanEvent Step 1 — renders ALL entries followed by a "More…" tile
 *     that focuses the existing event-type search field.
 *
 * Adding/removing/reordering entries here ripples to both surfaces
 * simultaneously by design (the previous architecture intentionally
 * shared the list; this file keeps that contract explicit).
 *
 * The "More…" tile is a UI affordance only — it MUST NOT be selected as an
 * `event_type` value. Consumers that map over this list and render tiles
 * should branch on `isMore === true` and handle the click separately
 * (focusing the search field). The `event_type` string field on the
 * `events` Mongo doc remains free-form and is never set to "__more__".
 *
 * Decision Reduction Engine templates (`services/eventTemplates.js`) are
 * keyed by these exact `value` strings. Adding a new entry here without a
 * matching template gracefully falls back to `FALLBACK_TEMPLATE` — no
 * runtime error.
 */
import {
  PartyPopper,
  Sparkles,
  Baby,
  GraduationCap,
  Heart,
  Diamond,
  Flower,
  Users,
  Wine,
  Building2,
  Plus,
} from 'lucide-react';

export const PRIMARY_EVENT_TYPES = [
  { value: 'Birthday Party',   label: 'Birthday',         icon: PartyPopper   },
  { value: 'Wedding',          label: 'Wedding',          icon: Sparkles      },
  { value: 'Baby Shower',      label: 'Baby Shower',      icon: Baby          },
  { value: 'Graduation Party', label: 'Graduation',       icon: GraduationCap },
  { value: 'Anniversary',      label: 'Anniversary',      icon: Heart         },
  { value: 'Engagement Party', label: 'Engagement',       icon: Diamond       },
  { value: 'Bridal Shower',    label: 'Bridal Shower',    icon: Flower        },
  { value: 'Family Reunion',   label: 'Family Reunion',   icon: Users         },
  { value: 'Private Party',    label: 'Private Party',    icon: Wine          },
  { value: 'Corporate Event',  label: 'Corporate',        icon: Building2     },
  { value: 'Church Event',     label: 'Church Event',     icon: Building2     },
  // Sentinel — UI affordance only. Never written to event_type.
  { value: '__more__',         label: 'More…',            icon: Plus,         isMore: true },
];

// Landing-page slice — preserves the existing six-tile hero composition.
// Driven off the same master registry so adding/reordering primary types
// stays a single-file change.
export const LANDING_EVENT_TYPES = PRIMARY_EVENT_TYPES.filter(t => !t.isMore).slice(0, 6);

// Secondary event types surfaced when the customer taps "More…" on Step 1.
// Every `value` here is an exact canonical string already present in the
// backend's `DEFAULT_EVENT_TYPES` (`backend/routes/events.py`) so vendor-side
// label resolution + custom-type tracking continue to behave normally. The
// Decision Reduction Engine (`services/eventTemplates.js`) gracefully falls
// back to `FALLBACK_TEMPLATE` for any entry without an explicit template —
// no runtime risk added by listing types here.
export const EXPANDED_EVENT_TYPES = [
  { value: 'Retirement Party',                label: 'Retirement Party'           },
  { value: 'Holiday Party',                   label: 'Holiday Party'              },
  { value: 'Housewarming',                    label: 'Housewarming'               },
  { value: 'Fundraiser / Charity Event',      label: 'Fundraiser'                 },
  { value: 'School Event',                    label: 'School Event'               },
  { value: 'Celebration of Life / Memorial',  label: 'Memorial / Celebration of Life' },
  { value: 'Gender Reveal',                   label: 'Gender Reveal'              },
  { value: 'Quinceañera / Sweet 16',          label: 'Quinceañera / Sweet 16'     },
  { value: 'Rehearsal Dinner',                label: 'Rehearsal Dinner'           },
  { value: 'Networking Event',                label: 'Networking Event'           },
  { value: 'Conference / Seminar',            label: 'Conference / Seminar'       },
];
